import * as React from "react";
import * as moment from "moment";
import { observer, inject } from "mobx-react";
import * as fileUtil from "../../common/Methods/FileUtil";
import BootstrapTable from "react-bootstrap-table-next";

import { NumberToMonetaryStringFormat } from "src/common/Methods/FileUtil";
import { Invoice, Proceeds, InvoiceLineItem, InvoiceTaxLineItem, SettlementItem, Payment } from "src/model/Financial";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { UncontrolledTooltip } from "reactstrap";
import { CustomerStore } from "src/stores/CustomerStore";
import { errorMessage } from "../common/Toast";

export const SimpleSettlementTable = inject("customerStore")(
    observer(
        ({
            customerStore,
            title,
            tableType,
            data,
            handleOnSelect,
            handleOnSelectAll,
            disableSelection,
            selected,
            keyField,
            settlementId,
            balance,
            downloadFile
        }: {
            title: string;
            tableType: "OTHER_DISBURSEMENTS" | "OTHER_PAYMENTS" | "PROCEEDS" | "INVOICES";
            data: any[];
            handleOnSelect?: (id: number, isSelect: boolean) => void;
            handleOnSelectAll?: (isSelect: boolean, rows: Array<number>) => void;
            selected?: number[];
            keyField: string;
            settlementId?: number;
            disableSelection?: boolean;
            balance?: number;
            downloadFile?: (id: number) => void;
            customerStore?: CustomerStore;
        }) => {
            const downloadInvoice = async (invoiceId: number) => {
                try {
                    const blob = await customerStore!.downloadInvoice([invoiceId]);
                    fileUtil.DownloadBlob(`Invoice_#${invoiceId}.pdf`, blob);
                } catch (error) {
                    errorMessage(error);
                }
            };

            const overpaymentsDisbursements = [
                {
                    dataField: "entrydate",
                    text: "Date",
                    formatter: (cellContent: any, row: Payment) => (row ? moment(row.entryDate).format("YYYY-MM-DD") : " - ")
                },
                {
                    dataField: "type",
                    text: "Type",
                    formatter: (type: string) => (type === "PAYMENT" ? "Payment" : "Disbursement")
                },
                {
                    dataField: "amount",
                    text: "Amount",
                    style: { textAlign: "right" },
                    headerStyle: { textAlign: "right" },
                    formatter: (cellContent: any, row: Payment) => "$" + NumberToMonetaryStringFormat(row.amountInCents)
                },
                {
                    dataField: "amountCovered",
                    text: "Used in Previous Settlement",
                    style: { textAlign: "right" },
                    headerStyle: { textAlign: "right" },
                    formatter: (cellContent: any, row: Payment) =>
                        "$" + NumberToMonetaryStringFormat(row.settlementItems.reduce((total, item) => total + item.amountInCents, 0))
                },
                {
                    dataField: "balance",
                    text: "Balance",
                    style: { textAlign: "right" },
                    headerStyle: { textAlign: "right" },
                    formatter: (cellContent: any, row: Payment) => "$" + NumberToMonetaryStringFormat(row.balance)
                }
            ];

            const proceeds = [
                {
                    dataField: "entryDate",
                    text: "Date",
                    formatter: (cellContent: any, row: Proceeds) => (row ? moment(row.entryDate).format("YYYY-MM-DD") : " - ")
                },
                {
                    dataField: "description",
                    text: "Description",
                    formatter: (cellContent: any, row: Proceeds) => (row.description ? row.description : "-")
                },
                {
                    dataField: "amountInCents",
                    text: "Amount",
                    style: { textAlign: "right" },
                    headerStyle: { textAlign: "right" },
                    formatter: (cellContent: any, row: Proceeds) => "$" + NumberToMonetaryStringFormat(row.amountInCents)
                },
                {
                    dataField: "amountInCentsPaid",
                    text: "Used in Previous Settlement",
                    style: { textAlign: "right" },
                    headerStyle: { textAlign: "right" },
                    formatter: (cellContent: any, row: Proceeds) =>
                        row.settlementItems.length > 0
                            ? "$" + NumberToMonetaryStringFormat(row.settlementItems.reduce((total, item) => total + item.amountInCents, 0))
                            : "$0"
                },
                {
                    dataField: "balance",
                    text: "Balance",
                    style: { textAlign: "right" },
                    headerStyle: { textAlign: "right" },
                    formatter: (cellContent: any, row: Proceeds) => "$" + NumberToMonetaryStringFormat(row.balance)
                }
            ];

            const invoices = [
                {
                    dataField: "invoiceNumber",
                    text: "Invoice #",
                    headerStyle: { width: "100px" },
                    formatter: (cellContent: any, row: Invoice) => {
                        return (
                            <div className="d-flex align-items-center">
                                <div>{row.id}</div>
                                <button
                                    type="button"
                                    id={`tooltipDownload${row.id}`}
                                    className="btn btn-secondary btn-sm ml-2"
                                    onClick={() => downloadInvoice(row.id)}
                                >
                                    <FontAwesomeIcon icon={faDownload} size="sm" />
                                </button>
                                <UncontrolledTooltip placement="top" target={`tooltipDownload${row.id}`} delay={0} className="tooltipstrap">
                                    Download Invoice
                                </UncontrolledTooltip>
                            </div>
                        );
                    }
                },
                {
                    dataField: "description",
                    text: "Invoice Description",
                    formatter: (cellContent: any, row: Invoice) => (row.description ? row.description : "-")
                },
                {
                    dataField: "amount",
                    text: "Total Amount",
                    style: { textAlign: "right" },
                    headerStyle: { textAlign: "right" },
                    formatter: (cellContent: any, row: Invoice) => {
                        const subtotal = row.lineItems.reduce(
                            (amount, item: InvoiceLineItem) => amount + item.amountInCents * (item.entryType.debitCredit === "CREDIT" ? -1 : 1),
                            0
                        );
                        const hst = row.invoiceTaxes.reduce((amount, item: InvoiceTaxLineItem) => amount + item.amountInCents, 0);
                        return "$" + NumberToMonetaryStringFormat(hst + subtotal);
                    }
                },
                {
                    dataField: "amountPayments",
                    text: "Covered in Previous Settlement",
                    style: { textAlign: "right" },
                    headerStyle: { textAlign: "right" },
                    formatter: (cellContent: any, row: Invoice) =>
                        row.settlementItems.length > 0
                            ? "$" + NumberToMonetaryStringFormat(row.settlementItems.reduce((total, item) => total + item.amountInCents, 0))
                            : "$0"
                },
                {
                    dataField: "balance",
                    text: "Balance",
                    formatter: (cellContent: any, row: Invoice) => "$" + NumberToMonetaryStringFormat(row.balance),
                    style: { textAlign: "right" },
                    headerStyle: { textAlign: "right" }
                }
            ];

            const returnColumns = (type: string) => {
                switch (type) {
                    case "INVOICES":
                        return invoices;
                        break;
                    case "OTHER_PAYMENTS":
                    case "OTHER_DISBURSEMENTS":
                        return overpaymentsDisbursements;
                        break;
                    case "PROCEEDS":
                        return proceeds;
                        break;
                    default:
                        return [];
                }
            };

            const selectableRows = () => {
                return settlementId ? data.map((i: Invoice) => i.id) : data.filter((i: Invoice) => i.balance === 0).map(i => i.id) || [];
            };

            const selectRow = {
                mode: "checkbox",
                clickToSelect: false,
                selected: !disableSelection ? selected : [],
                onSelect: (object: any, isSelect: boolean) => {
                    if (!disableSelection && handleOnSelect) {
                        handleOnSelect(object.id, isSelect);
                    }
                },
                onSelectAll: (isSelect: boolean, rows: Array<any>) => {
                    console.log(rows);
                    if (!disableSelection && handleOnSelectAll) {
                        handleOnSelectAll(
                            isSelect,
                            rows.map(i => i.id)
                        );
                    }
                },
                hideSelectColumn: disableSelection,
                nonSelectable: disableSelection ? data.map(i => i.id) : selectableRows()
            };

            return (
                <>
                    <div className="text-center bg-dark text-light">{title}</div>
                    <BootstrapTable
                        keyField="id"
                        striped={false}
                        remote={true}
                        hover={false}
                        bordered={false}
                        // TODO: consider if this approach is OK
                        columns={returnColumns(tableType)}
                        classes="table customTable table-sm mb-0"
                        data={data}
                        noDataIndication={() => (
                            <div>
                                <span>No information.</span>
                            </div>
                        )}
                        selectRow={selectRow}
                    />
                    <div className="row mb-3" style={{ marginLeft: "0" }}>
                        <div className="col-12 py-2 px-0 text-right d-flex">
                            <div className="ml-auto">Total: </div>
                            <div className="text-right" style={{ minWidth: "100px" }}>
                                {// TODO CHECK BALANCE = 0
                                "$" + NumberToMonetaryStringFormat(balance ? balance : data.reduce((total, item) => total + item.balance, 0))}
                            </div>
                        </div>
                    </div>
                </>
            );
        }
    )
);



// WEBPACK FOOTER //
// ./src/components/customer/SimpleSettlementTable.tsx